export type tAutoMessage = {
  tiktokAutoMessageId: string;
  autoMessageType:
    'WELCOME_MESSAGE' | 'SUGGESTED_QUESTION' | 'CHAT_PROMPT' | 'FOLLOW_WELCOME';
  auditStatus: 'reviewing' | 'approved' | 'rejected';
  content: tWelcomeContent | tQuestionContent | tPromptContent | tFollowContent;
  position: number;
  insertedAt: string;
  updatedAt: string;
};

export type tWelcomeContent = { content: string };
export type tQuestionContent = { question: string; answer: string };
export type tPromptContent = { title: string; content: string };

export type tMessagingState = 'ok' | 'not_eligible' | 'temporary' | 'internal';

export type tMessagingStatus = {
  state: tMessagingState;
  reason: string | null;
  providerCode: number | null;
  providerMessage: string | null;
  checkedAt: string | null;
};

export type tTikTokFeature =
  'welcome' | 'questions' | 'prompts' | 'commentToMessage' | 'followWelcome';
export type tFeatureStatuses = Record<tTikTokFeature, tMessagingStatus>;

export type tAutoMessagesResponse = {
  capability?: boolean | null;
  buttonOptions?: tFollowButton[] | null;
  enabled: boolean;
  autoMessages: tAutoMessage[];
  // False when the backend couldn't reconcile with TikTok's Message API and is
  // instead serving its last-known local snapshot (e.g. the account lost Business
  // eligibility). The data returned is still accurate, just possibly stale.
  syncOk: boolean;
  // Optional on purpose: an older bff-web / tiktok-service omits it entirely.
  // Absent means `ok` — never a problem — so a partial rollout paints no false alarm.
  messagingStatus?: tMessagingStatus;
};

export type tAutoMessageResponse = tAutoMessage;

// The sort endpoint's response only ever carries the reordered list — no
// `enabled`/`syncOk` (see autoMessagesSortData in bff-web). A separate type
// keeps that honest instead of overclaiming tAutoMessagesResponse's shape.
export type tAutoMessagesSortResponse = {
  autoMessages: tAutoMessage[];
};

export type tCreateAutoMessageRequest = {
  auto_message_type: string;
  content: tWelcomeContent | tQuestionContent | tPromptContent | tFollowContent;
};

export type tCommentToMessageResponse = {
  enabled: boolean;
  syncOk: boolean;
  // Optional on purpose: an older bff-web / tiktok-service omits it entirely.
  // Absent means `ok` — never a problem — so a partial rollout paints no false alarm.
  messagingStatus?: tMessagingStatus;
};

export type tToggleResponse = {
  ok: boolean;
};

export type tOAuthAuthorizeResponse = {
  redirectUrl: string;
};

export const AUTO_MESSAGE_LIMITS = {
  WELCOME_MESSAGE: { max: 1, contentMax: 250 },
  SUGGESTED_QUESTION: { max: 3, questionMax: 80, answerMax: 200 },
  CHAT_PROMPT: { max: 6, titleMax: 18, contentMax: 150 },
} as const;

export type tAutoMessagesSection = {
  enabled: boolean;
  messages: tAutoMessage[];
  syncOk: boolean;
  // Optional on purpose: an older bff-web / tiktok-service omits it entirely.
  // Absent means `ok` — never a problem — so a partial rollout paints no false alarm.
  messagingStatus?: tMessagingStatus;
};

export type tTikTokContext = {
  isLoading: boolean;
  isAutoMessagesLoading: boolean;
  isConnected: boolean;
  // Aggregate is used only to recover account-wide authorization errors.
  messagingStatus: tMessagingStatus;
  featureStatuses: tFeatureStatuses;
  // Re-runs the four section reads in place (no skeleton).
  refetch: () => void;
  commentToMessageEnabled: boolean;
  welcomeMessages: tAutoMessagesSection;
  suggestedQuestions: tAutoMessagesSection;
  chatPrompts: tAutoMessagesSection;
  toggleCommentToMessage: (enabled: boolean) => Promise<void>;
  // Resolve false after the error toast, so a form can stay open with what was written.
  createAutoMessage: (
    type: string,
    content: tCreateAutoMessageRequest['content'],
  ) => Promise<boolean>;
  updateAutoMessage: (
    id: string,
    content: tCreateAutoMessageRequest['content'],
  ) => Promise<boolean>;
  deleteAutoMessage: (id: string) => Promise<boolean>;
  toggleAutoMessageType: (type: string, enabled: boolean) => Promise<void>;
  sortAutoMessages: (type: string, ids: string[]) => Promise<void>;
  // The new follower message (FOLLOW_WELCOME), with what TikTok lets this account offer.
  followWelcome: tFollowSection;
  // The account as TikTok shows it, read on every load; null while unknown or when the read failed.
  profile: tTikTokProfile | null;
  // Only `disconnected` means the account left; every other answer keeps it connected.
  disconnect: (options: { disableAutomaticMessages: boolean }) => Promise<tDisconnectResult>;
};

export type tFollowSection = tAutoMessagesSection & {
  // null: TikTok has not said (the read failed or an older backend omits it).
  capability: boolean | null;
  buttonOptions: tFollowButton[];
};

// GET /tiktok-api/accounts/profile (tiktok-service docs/contracts/account-management.md). Every
// identity field can be null: the grant may lack the scope, and missing is null, never false.
export type tTikTokProfile = {
  open_id: string;
  authorization_version: string;
  authorized_at: string | null;
  checked_at: string;
  display_name: string | null;
  username: string | null;
  // Signed and temporary: shown, never stored.
  profile_image: string | null;
  profile_image_expires_at: string | null;
  profile_deep_link: string | null;
  is_verified: boolean | null;
  // TikTok's Verified Business Account, not DM eligibility: never a claim on the page (BUG-1143).
  is_business_account: boolean | null;
  has_advanced_access: boolean | null;
  missing_scopes: string[];
};

// One automatic message type the disconnect tried to turn off first.
export type tAutoMessageShutdown = {
  type: tAutoMessage['autoMessageType'];
  disabled: boolean;
  status: tMessagingStatus | null;
};

export type tDisconnectResult =
  | { kind: 'disconnected' }
  // 409 AUTO_MESSAGES_DISABLE_FAILED: some types are still on, so nothing was revoked.
  | { kind: 'cleanupFailed'; results: tAutoMessageShutdown[] }
  // 424 DISCONNECT_FAILED: the cleanup finished, the revoke did not.
  | { kind: 'revokeFailed'; results: tAutoMessageShutdown[] }
  // 409 STALE_AUTHORIZATION: the grant changed since it was read.
  | { kind: 'stale' }
  | { kind: 'failed' };

export type tFollowContent = {
  message: string;
  interaction: 'NONE' | 'QUICK_REPLY' | 'QUICK_ACTION';
  quick_reply_text?: string;
  quick_action_type?: 'SHOP' | 'JOIN_GROUP';
};
export type tFollowButton = {
  interaction: string;
  quick_action_type?: string;
  button_status: 'ENABLE' | 'NO_PERMISSION' | 'NO_DATA';
};
export type tTikTokPermissions = {
  open_id: string;
  authorization_version: string;
  scopes: string[];
};
export type tTikTokRichContent =
  | { kind: 'share_post'; share_post: { item_id: string } }
  | {
      kind: 'template';
      template: {
        type: 'QA_BUTTON_CARD' | 'QA_LINK_CARD';
        title: string;
        buttons: { type: 'REPLY'; title: string; id?: string }[];
      };
    };

export function parseTikTokPostId(value: string): string | null {
  const input = value.trim();
  if (/^\d{10,30}$/.test(input)) return input;
  try {
    const url = new URL(input);
    if (
      url.protocol !== 'https:' ||
      !['www.tiktok.com', 'tiktok.com'].includes(url.hostname) ||
      url.username ||
      url.password
    )
      return null;
    return url.pathname.match(/^\/@[^/]+\/video\/(\d{10,30})\/?$/)?.[1] ?? null;
  } catch {
    return null;
  }
}
